import React, { useState } from "react";
import {
  AppBar,
  Box,
  Drawer,
  FormControl,
  Link,
  MenuItem,
  Select,
  Toolbar,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import {
  logo,
  logoLogged,
  info,
  infoLogged,
  menu,
  menuLogged,
  drawerClose,
  arrowDownIcon,
} from "assets/images";
import { Button } from "components/Shared/Button";
import RoutePaths from "routes/RoutePaths";
import { useAuthSelectors } from "redux/selectors";
import { useAppDispatch } from "redux/store/hooks";
import {
  Language,
  setBottomBarAction,
  setLanguage,
  Roles,
  setIsOpenUseEDUBAModal,
  IIsExpertApproved,
} from "redux/reducers";
import HeaderBarPopover from "./ProfilePopover";
import "./styles.scss";

interface HeaderBarProps {
  children?: React.ReactElement[] | React.ReactNode;
}

const HeaderBar: React.FC<HeaderBarProps> = ({ children }) => {
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();
  const { push } = useHistory();
  const location = useLocation();
  const { selectAuthUser } = useAuthSelectors();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [open2, setOpen2] = useState(false);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen2(newOpen);
  };

  let currentLogo: string;
  if (selectAuthUser) {
    currentLogo = open2 ? logo : logoLogged;
  } else {
    currentLogo = logo;
  }

  const handleProfileClick = (event: any) => {
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  };

  function changeLanguage(language: Language) {
    dispatch(setLanguage(language));
    i18n.changeLanguage(language);
  }

  const handleLogoOnClick = () => {
    dispatch(setBottomBarAction(0));
    push(RoutePaths.root);
  };

  const pathname = window?.location?.pathname || "";

  const renderMenuItems = () => {
    if (selectAuthUser?.currentRole === Roles.student)
      return (
        <div className="menu-item">
          <MenuItem
            className={pathname === RoutePaths.root ? "selected" : ""}
            onClick={() => push(RoutePaths.root)}
          >
            {t("headerBar.menu.home")}
          </MenuItem>
          <MenuItem
            className={pathname === RoutePaths.studentLessons ? "selected" : ""}
            onClick={() => push(RoutePaths.studentLessons)}
          >
            {t("headerBar.menu.lesson")}
          </MenuItem>
          <MenuItem
            className={
              pathname === RoutePaths.favouriteExperts ||
              pathname === RoutePaths.allExperts
                ? "selected"
                : ""
            }
            onClick={() => push(RoutePaths.favouriteExperts)}
          >
            {t("headerBar.menu.teacher")}
          </MenuItem>
          <MenuItem
            className={
              pathname === RoutePaths.studentPurchasePoints ? "selected" : ""
            }
            onClick={() => push(RoutePaths.studentPurchasePoints)}
          >
            {t("headerBar.menu.purchasePoints")}
          </MenuItem>
          <MenuItem
            className="popup"
            onClick={() => dispatch(setIsOpenUseEDUBAModal(true))}
          >
            <span className={`info ${selectAuthUser ? "logged" : ""}`}>
              <img src={selectAuthUser ? infoLogged : info} alt="" />
              {t("headerBar.menu.useEDUBA")}
            </span>
          </MenuItem>
        </div>
      );
    else if (
      selectAuthUser?.currentRole === Roles.expert &&
      selectAuthUser?.isExpertApproved === IIsExpertApproved.APPROVED
    )
      return (
        <div className="menu-item">
          <MenuItem
            className={pathname === RoutePaths.root ? "selected" : ""}
            onClick={() => push(RoutePaths.root)}
          >
            {t("headerBar.menu.home")}
          </MenuItem>
          <MenuItem
            className={
              pathname === RoutePaths.lessons ||
              pathname === RoutePaths.createSchedule ||
              pathname === RoutePaths.reservedLessons
                ? "selected"
                : ""
            }
            onClick={() => push(RoutePaths.lessons)}
          >
            {t("headerBar.menu.lessonCard")}
          </MenuItem>
          <MenuItem
            className={
              pathname === RoutePaths.expertRevenueManagement ? "selected" : ""
            }
            onClick={() => push(RoutePaths.expertRevenueManagement)}
          >
            {t("headerBar.menu.income")}
          </MenuItem>
          <MenuItem
            className={pathname === RoutePaths.expertHome ? "selected" : ""}
            onClick={() => push(RoutePaths.expertHome)}
          >
            {t("headerBar.menu.profile")}
          </MenuItem>
        </div>
      );
    else if (
      selectAuthUser?.currentRole === Roles.expert &&
      selectAuthUser?.isExpertApproved !== IIsExpertApproved.APPROVED
    )
      return (
        <div className="menu-item">
          <MenuItem
            className={pathname === RoutePaths.root ? "selected" : ""}
            onClick={() => push(RoutePaths.root)}
          >
            {t("headerBar.menu.home")}
          </MenuItem>
          <MenuItem
            className={pathname === RoutePaths.expertHome ? "selected" : ""}
            onClick={() => push(RoutePaths.expertHome)}
          >
            {t("headerBar.menu.profile")}
          </MenuItem>
        </div>
      );

    return (
      <div className="menu-item">
        <MenuItem className={"selected"} onClick={() => push(RoutePaths.root)}>
          {t("headerBar.menu.home")}
        </MenuItem>
        <MenuItem onClick={() => dispatch(setIsOpenUseEDUBAModal(true))}>
          <span className={`info ${selectAuthUser ? "logged" : ""}`}>
            <img src={selectAuthUser ? infoLogged : info} alt="" />
            {t("headerBar.menu.useEDUBA")}
          </span>
        </MenuItem>
      </div>
    );
  };

  return (
    <Box
      className={
        open2 ? "header-bar-container drawer-header" : "header-bar-container"
      }
    >
      <AppBar position="static" className={selectAuthUser ? "logged" : ""}>
        <Toolbar>
          <img
            src={currentLogo}
            className="logo"
            alt=""
            onClick={() => handleLogoOnClick()}
          />
          {children}
          <div className="right-container">
            {renderMenuItems()}
            <div className="row-reverse">
              {selectAuthUser && (
                <div className="user-wrapper">
                  <div className="wrap-area">
                    <img
                      className="user"
                      onClick={(e) => handleProfileClick(e)}
                      loading="lazy"
                      src={selectAuthUser?.thumbnail}
                      referrerPolicy="no-referrer"
                      alt=""
                      aria-hidden={true}
                      data-noaft=""
                    />
                  </div>
                </div>
              )}
              {!selectAuthUser && location.pathname !== RoutePaths.login && (
                <Button
                  id="login-button"
                  customStyles="btn-login"
                  label={t("headerBar.label.login")}
                  onClick={() => push(RoutePaths.login)}
                />
              )}
              <FormControl className="form-control">
                <Select
                  className="lang-select"
                  defaultValue="en"
                  id="header-bar-translate-button"
                  value={i18n.language}
                >
                  <MenuItem
                    className="menu"
                    value="en"
                    onClick={() => changeLanguage("en")}
                  >
                    <span className="full-text">English</span>
                    <span className="short-text">EN</span>
                  </MenuItem>
                  <MenuItem
                    className="menu"
                    value="jp"
                    onClick={() => changeLanguage("jp")}
                  >
                    <span className="full-text">日本語</span>
                    <span className="short-text">日本</span>
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
            {selectAuthUser ? (
              <img
                src={menuLogged}
                alt=""
                className="menu-icon"
                onClick={toggleDrawer(true)}
              />
            ) : (
              <img
                src={menu}
                alt=""
                className="menu-icon"
                onClick={toggleDrawer(true)}
              />
            )}
            <Drawer
              className="menu-drawer"
              anchor="top"
              open={open2}
              onClose={toggleDrawer(false)}
            >
              <img
                src={drawerClose}
                className="drawer-close"
                alt=""
                onClick={toggleDrawer(false)}
              />
              <ul className="menu-ul-list" onClick={toggleDrawer(false)}>
                <li className="lg">
                  <Link onClick={() => handleLogoOnClick()}>
                    {t("headerBar.menu.home")}
                  </Link>
                </li>
                {selectAuthUser?.currentRole === Roles.student && (
                  <React.Fragment>
                    <li className="lg">
                      <Link onClick={() => push(RoutePaths.favouriteExperts)}>
                        <img
                          src={arrowDownIcon}
                          className="menu-icon ms-0"
                          alt=""
                        />
                        {t("staticBottomBar.teacher")}
                      </Link>
                    </li>
                    <li className="lg">
                      <Link onClick={() => push(RoutePaths.studentLessons)}>
                        <img
                          src={arrowDownIcon}
                          className="menu-icon ms-0"
                          alt=""
                        />
                        {t("staticBottomBar.lesson")}
                      </Link>
                    </li>
                    <li className="lg">
                      <Link
                        onClick={() => push(RoutePaths.studentPurchasePoints)}
                      >
                        <img
                          src={arrowDownIcon}
                          className="menu-icon ms-0"
                          alt=""
                        />
                        {t("staticBottomBar.purchasePoints")}
                      </Link>
                    </li>
                  </React.Fragment>
                )}
                {selectAuthUser?.currentRole === Roles.expert &&
                  selectAuthUser?.isExpertApproved && (
                    <React.Fragment>
                      <li className="lg">
                        <Link onClick={() => push(RoutePaths.expertHome)}>
                          <img
                            src={arrowDownIcon}
                            className="menu-icon ms-0"
                            alt=""
                          />
                          {t("staticBottomBar.teacher")}
                        </Link>
                      </li>
                      <li className="lg">
                        <Link onClick={() => push(RoutePaths.lessons)}>
                          <img
                            src={arrowDownIcon}
                            className="menu-icon ms-0"
                            alt=""
                          />
                          {t("staticBottomBar.lesson")}
                        </Link>
                      </li>
                    </React.Fragment>
                  )}
                <li>
                  <Link onClick={() => push(RoutePaths.howToUseGuide)}>
                    {t("footer.links.howToUse")}
                  </Link>
                </li>
                <li>
                  <Link onClick={() => push(RoutePaths.paymentPolicy)}>
                    {t("footer.links.paymentPolicyPage")}
                  </Link>
                </li>
                <li>
                  <Link onClick={() => push(RoutePaths.InstructorPolicy)}>
                    {t("footer.links.teacherPolicyPage")}
                  </Link>
                </li>
                <li>
                  <Link onClick={() => push(RoutePaths.cancellationPolicy)}>
                    {t("footer.links.cancelPolicy")}
                  </Link>
                </li>
                <li>
                  <Link onClick={() => push(RoutePaths.privacyPolicy)}>
                    {t("footer.links.privacyPolicy")}
                  </Link>
                </li>
                {selectAuthUser && (
                  <li>
                    <Link onClick={() => push(RoutePaths.contactForm)}>
                      {t("footer.links.contactUs")}
                    </Link>
                  </li>
                )}
                <li>
                  <Link onClick={() => push(RoutePaths.termsOfService)}>
                    {t("footer.links.termsOfService")}
                  </Link>
                </li>
                <li>
                  <Link onClick={() => push(RoutePaths.transactionLaw)}>
                    {t("footer.links.transactionLaw")}
                  </Link>
                </li>
              </ul>
              {!selectAuthUser && location.pathname !== RoutePaths.login && (
                <Button
                  id="login-button"
                  customStyles="btn-login"
                  label={t("headerBar.label.login")}
                  onClick={() => push(RoutePaths.login)}
                />
              )}
            </Drawer>
          </div>
        </Toolbar>
      </AppBar>
      {selectAuthUser && (
        <HeaderBarPopover open={open} setOpen={setOpen} anchorEl={anchorEl} />
      )}
    </Box>
  );
};

export default HeaderBar;
